import { useCallback, cloneElement, isValidElement } from 'react';
import type { ReactNode } from 'react';

import { useSelector } from 'react-redux';

import { getTranslatedString } from '../../selectors';

import type { State } from '../../selectors/types';

const MARKER_PREFIX = '__ELEMENT_';
const MARKER_SUFFIX = '__';

export function useTranslation() {
  const localisation = useSelector((s: State) => s.localisation);

  const translate = useCallback(
    (key: string, context?: Record<string, string> | null): string =>
      getTranslatedString({ localisation } as State, key, context),
    [localisation],
  );

  /**
   * Translates a string with React element substitutions (e.g., adding styled spans).
   *
   * Use this for inserting React elements like <span className="...">, <strong>, etc.
   * DO NOT use this for rendering HTML strings - it does not use dangerouslySetInnerHTML.
   * All substitutions should be proper React elements, not HTML strings.
   *
   * @example
   * tWithElements('key_with_placeholder', {
   *   price: <span className="price">{formattedPrice}</span>,
   * })
   */
  const translateWithElements = useCallback(
    (
      key: string,
      substitutions: Record<string, ReactNode>,
    ): Array<string | ReactNode> => {
      // Create marker substitutions for translation
      const markerSubstitutions: Record<string, string> = {};
      const elementMap: Record<string, ReactNode> = {};

      Object.keys(substitutions).forEach((subKey) => {
        const marker = `${MARKER_PREFIX}${subKey}${MARKER_SUFFIX}`;
        markerSubstitutions[subKey] = marker;
        elementMap[marker] = substitutions[subKey];
      });

      // Get translated string with markers
      const translated = getTranslatedString(
        { localisation } as State,
        key,
        markerSubstitutions,
      );

      // Split by markers and reconstruct with React elements
      const parts: Array<string | ReactNode> = [];

      // Find all marker positions (including repeated occurrences) and sort by position
      const markerPositions: Array<{ marker: string; index: number }> = [];
      Object.keys(elementMap).forEach((marker) => {
        let searchIndex = 0;
        let foundIndex = translated.indexOf(marker, searchIndex);
        while (foundIndex !== -1) {
          markerPositions.push({ marker, index: foundIndex });
          searchIndex = foundIndex + marker.length;
          foundIndex = translated.indexOf(marker, searchIndex);
        }
      });
      markerPositions.sort((a, b) => a.index - b.index);

      let lastIndex = 0;

      markerPositions.forEach(({ index, marker }) => {
        // Add text before marker
        if (index > lastIndex) {
          parts.push(translated.substring(lastIndex, index));
        }
        // Add the React element with a unique key (marker + position for uniqueness)
        const element = elementMap[marker];
        if (isValidElement(element)) {
          parts.push(cloneElement(element, { key: `${marker}-${index}` }));
        } else {
          parts.push(element);
        }
        // Update position for next iteration
        lastIndex = index + marker.length;
      });

      // Add any remaining text
      if (lastIndex < translated.length) {
        parts.push(translated.substring(lastIndex));
      }

      return parts;
    },
    [localisation],
  );

  /**
   * returns a translation function with both simple and element substitution methods.
   * @example
   * const { tWithElements } = useTranslation();
   * OR
   * const translate = useTranslation();
   */
  const translationFunction = Object.assign(translate, {
    tWithElements: translateWithElements,
  });

  return translationFunction;
}
